// Editing an assigned practice set (2026-10-08, PLAN.md Part 3). Pure helpers
// for the Edit panel: what a set looks like read-only, what the teacher may
// change, and the exact updateHomework payload (changed fields only).
// Locked after assigning: subject, coverage, topics, levels, question count.
// The start time (2026-10-09) can change only while the set is not open yet.
import catalog from './homeworkTopicsV4.json';
import { dueEndMs } from './unitPlan';

const DOMAIN_LABELS = {
  algebra: 'Algebra',
  'advanced-math': 'Advanced Math',
  'problem-solving-and-data-analysis': 'Problem Solving & Data Analysis',
  'geometry-and-trigonometry': 'Geometry & Trigonometry',
  'information-and-ideas': 'Information & Ideas',
  'craft-and-structure': 'Craft & Structure',
  'expression-of-ideas': 'Expression of Ideas',
  'standard-english-conventions': 'Standard English Conventions',
};
const TOPIC_LABELS = new Map([...catalog.math, ...catalog.reading].map((t) => [t.id, t.label]));

export const TITLE_MAX = 80;

export const subjectLabel = (subject) => (subject === 'math' ? 'Math' : subject === 'both' ? 'Math + Reading & Writing' : 'Reading & Writing');
export const domainLabel = (domainId) => DOMAIN_LABELS[domainId] || domainId || '';
export const topicLabel = (topicId) => TOPIC_LABELS.get(topicId) || topicId;

// Whole-class sets list the roster minus excludeIds; picked-student sets list
// studentIds. Missing arrays read as "whole class" (older rows).
export const isPickedSet = (hw) => Array.isArray(hw?.studentIds) && hw.studentIds.length > 0;

// Read-only summary lines for the locked fields. Works for every selection
// version: v4 rows carry coverage/topicIds/levelMode; v1/v3 rows carry mode,
// domainId and concept lists; v5 (2026-10-17) rows are Curriculum Connect
// assessments: a unit's lessons, the same questions for everyone.
export function lockedSummary(hw) {
  const subject = subjectLabel(hw.subject);
  const n = (list) => (Array.isArray(list) ? list.length : 0);
  let coverage;
  if (hw.coverage === 'curriculum') {
    const c = hw.curriculum || {};
    const codes = (Array.isArray(c.lessons) ? c.lessons : []).map((l) => l?.code).filter(Boolean);
    const unit = c.unitN != null ? `Unit ${c.unitN}${c.unitTitle ? ` ${c.unitTitle}` : ''}` : 'Curriculum lessons';
    coverage = codes.length ? `${unit} · ${codes.length === 1 ? 'lesson' : 'lessons'} ${codes.join(', ')}` : unit;
    return { subject, coverage, levels: 'Same questions for everyone', count: Number(hw.count || 0) };
  }
  if (hw.selectionVersion === 4 || hw.coverage) {
    if (hw.coverage === 'topics' && n(hw.topicIds)) coverage = hw.topicIds.map(topicLabel).join(', ');
    else if (hw.coverage === 'domain' && hw.domainId) coverage = domainLabel(hw.domainId);
    else coverage = hw.subject === 'both' ? 'Whole SAT' : `Whole ${subject}`;
  } else if (hw.mode === 'subject') coverage = `All ${subject}`;
  else if (n(hw.questionConceptIds) || n(hw.conceptIds)) {
    const ids = n(hw.questionConceptIds) ? hw.questionConceptIds : hw.conceptIds;
    coverage = `${ids.length} concept${ids.length === 1 ? '' : 's'}${hw.domainId ? ` in ${domainLabel(hw.domainId)}` : ''}`;
  } else if (n(hw.bandSkillIds)) coverage = `${hw.bandSkillIds.length} skill${hw.bandSkillIds.length === 1 ? '' : 's'}${hw.domainId ? ` in ${domainLabel(hw.domainId)}` : ''}`;
  else coverage = hw.domainId ? domainLabel(hw.domainId) : `All ${subject}`;
  let levels;
  if (hw.levelMode === 'range' && hw.levelMin != null && hw.levelMax != null) {
    levels = hw.levelMin === hw.levelMax ? `Level ${hw.levelMin}` : `Levels ${hw.levelMin}-${hw.levelMax}`;
  } else if (hw.conceptLevels && typeof hw.conceptLevels === 'object' && Object.keys(hw.conceptLevels).length) levels = 'Levels you picked';
  else levels = 'Each student’s level';
  return { subject, coverage, levels, count: Number(hw.count || 0) };
}

// ---- due dates ----
// datetime-local value ('2026-10-09T15:30') for a stored due (ISO with a
// time, or a legacy date-only string which implies 11:59 PM local).
export function dueLocalValue(due) {
  if (!due) return '';
  const s = String(due);
  const d = s.includes('T') ? new Date(s) : new Date(`${s}T23:59:00`);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (x) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
export function dueIsoFromLocal(local) {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}
export const dueLocalIsValid = (local) => !local || dueIsoFromLocal(local) != null;
export const dueLocalIsPast = (local, now = Date.now()) => !!local && dueLocalIsValid(local) && new Date(local).getTime() < now;
export const dueIsPast = (due, now = Date.now()) => !!due && dueEndMs(due) < now;
// A set with a future start is not open to students yet; only then can the start move.
export const notOpenYet = (hw, now = Date.now()) => !!hw?.startAt && Date.parse(hw.startAt) > now;

// Two dues are the same moment (a reloaded date-only due equals its 11:59 PM form).
const sameDue = (local, stored) => dueLocalValue(stored) === (local || '');

// The students this set currently goes to (ids), and the roster entries it
// could add. roster = getRoster students; set rows = { userId, name, removed }.
export function studentRows(hw, roster) {
  const byId = new Map((roster || []).map((s) => [s.userId, s]));
  const nameOf = (id) => byId.get(id)?.name || id;
  if (isPickedSet(hw)) {
    const ids = [...new Set(hw.studentIds)];
    const current = ids.map((id) => ({ userId: id, name: nameOf(id), enrolled: byId.has(id) }));
    const addable = (roster || []).filter((s) => !hw.studentIds.includes(s.userId)).map((s) => ({ userId: s.userId, name: s.name }));
    return { picked: true, current, addable };
  }
  const excluded = new Set(hw.excludeIds || []);
  const current = (roster || []).map((s) => ({ userId: s.userId, name: s.name, enrolled: true, removed: excluded.has(s.userId) }));
  return { picked: false, current, addable: [] };
}

// Changed fields only (the caller adds classID). draft = { title, dueLocal,
// closeAtDue, startLocal?, add: Set, remove: Set }. startLocal only counts while
// the set is not open yet ('' = open it now). rev defaults to 0 for rows written
// before editing existed (the server treats a missing rev as 0).
export function editPayload(hw, draft) {
  const out = { hwId: hw.hwId, rev: Number.isInteger(hw.rev) ? hw.rev : 0 };
  const title = String(draft.title ?? '').trim();
  if (title && title !== String(hw.title || '').trim()) out.title = title;
  const dueChanged = !sameDue(draft.dueLocal, hw.dueDate);
  if (dueChanged) out.dueDate = dueIsoFromLocal(draft.dueLocal);
  const hasDue = !!draft.dueLocal;
  const closeAtDue = hasDue && !!draft.closeAtDue;
  if (closeAtDue !== !!hw.closeAtDue) out.closeAtDue = closeAtDue;
  if (draft.startLocal !== undefined && notOpenYet(hw) && draft.startLocal !== dueLocalValue(hw.startAt)) {
    out.startAt = draft.startLocal ? dueIsoFromLocal(draft.startLocal) : null;
  }
  const add = [...(draft.add || [])];
  const remove = [...(draft.remove || [])];
  if (add.length) out.addStudentIds = add;
  if (remove.length) out.removeStudentIds = remove;
  return out;
}
export const hasEdits = (payload) => Object.keys(payload).some((k) => !['hwId', 'rev'].includes(k));

// Client-side mirror of the server's checks; the server stays the authority.
export function validateDraft(hw, draft, rows) {
  const title = String(draft.title ?? '').trim();
  if (!title) return 'Give the assignment a title.';
  if (title.length > TITLE_MAX) return `Keep the title under ${TITLE_MAX} characters.`;
  if (!dueLocalIsValid(draft.dueLocal)) return 'That due date is not valid.';
  if (draft.startLocal && notOpenYet(hw) && draft.startLocal !== dueLocalValue(hw.startAt)) {
    if (!dueLocalIsValid(draft.startLocal)) return 'That start time is not valid.';
    if (new Date(draft.startLocal).getTime() <= Date.now()) return 'That start time has passed. Pick a later one, or open the set now.';
  }
  if (draft.startLocal && draft.dueLocal && notOpenYet(hw) && new Date(draft.dueLocal).getTime() <= new Date(draft.startLocal).getTime()) return 'The due date must be after the start.';
  // Only a picked-student set can run out of students (the server's rule too).
  // A whole-class set keeps reaching anyone who joins later, so removing every
  // current student from it is allowed.
  if (rows?.picked) {
    const left = rows.current.filter((s) => !draft.remove?.has(s.userId)).length + (draft.add?.size || 0);
    if (left === 0) return 'A practice set needs at least one student. Delete the assignment instead.';
  }
  return '';
}
